import {LngKeys} from "@app/shared/enums";
import {AnalyticsService} from "@app/shared/services";
import {RouterLink} from "@angular/router";
import {LngRoutePipe} from "@app/shared/pipes";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {route_paths} from "@app/shared/constants/languages_data";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {AppStateService} from "@app/shared/services/app-state.service";
import {addSeoSchemaScript, removeSeoSchemaScript} from "@app/shared/helpers/seo-schema-script";
import {TextContent, TextContent_faq} from "@app/shared/interfaces/seo-schema-script-types";
import {get_seo_schema_app, get_seo_schema_website, seo_shema_org} from "@app/shared/constants/seo-schema";
import {FAQSectionComponent} from "@app/layouts/main-layout/pages/sections/faq-section/faq-section.component";
import {afterNextRender, Component, DestroyRef, DOCUMENT, inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {
  MoreThenSectionComponent
} from "@app/layouts/main-layout/pages/sections/more-then-section/more-then-section.component";
import {
  ResumeExamplesSectionComponent
} from "@app/layouts/main-layout/pages/sections/resume-examples-section/resume-examples-section.component";
import {
  WhyUseUsSectionComponent
} from "@app/layouts/main-layout/pages/sections/why-use-us-section/why-use-us-section.component";
import {
  ResumeExamplesBannerComponent
} from "@app/shared/ui-kits/resume-examples-banner/resume-examples-banner.component";
import {UsersReviewComponent} from "@app/shared/ui-kits/users-review/users-review.component";
import {UsersReviewGridComponent} from "@app/shared/ui-kits/users-review-grid/users-review-grid.component";

@Component({
  selector: 'sf-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  imports: [
    RouterLink,
    LngRoutePipe,
    TranslateModule,
    FAQSectionComponent,
    WhyUseUsSectionComponent,
    MoreThenSectionComponent,
    ResumeExamplesBannerComponent,
    ResumeExamplesSectionComponent,
    UsersReviewComponent,
    UsersReviewGridComponent,
  ]
})
export class HomePageComponent implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly renderer2 = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analyticsService = inject(AnalyticsService);
  protected readonly appStateService = inject(AppStateService);
  protected readonly translateService = inject(TranslateService);

  protected readonly resumeBuildSteps = [
    {
      title: 'builder_steps.title1',
      desc: 'builder_steps.desc1',
      icon: 'sf-icon-pick-template'
    },
    {
      title: 'builder_steps.title2',
      desc: 'builder_steps.desc2',
      icon: 'sf-icon-layout-customize'
    },
    {
      title: 'builder_steps.title3',
      desc: 'builder_steps.desc3',
      icon: 'sf-icon-fill-blanks'
    },
    {
      title: 'builder_steps.title4',
      desc: 'builder_steps.desc4',
      icon: 'sf-icon-download'
    },
  ];

  private readonly seoSchemaName = 'home-page-seo-schema';

  protected trackResumeAction(action: 'manual_builder' | 'ai_generator' | 'ats_checker') {
    this.analyticsService.track('homepage_resume_cta_clicked', {
      action,
      placement: 'hero',
      language: this.translateService.currentLang || LngKeys.EN,
      is_authenticated: this.appStateService.isAuthenticated
    });
  }

  constructor() {
    afterNextRender(() => {
      this.translateService.onLangChange
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.addSeoSchemaScript();
        });
    });
  }

  ngOnInit() {
    this.appStateService.setPageSeo('home', this.renderer2);

    this.addImagePriority();


    // 1. Wait until the language is loaded and translations are ready
    this.translateService.get('seo_schema.home_page.faq.q1_name')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // 2. Safely generate and inject your schema once data is in memory
        this.addSeoSchemaScript();
      });


  }

  ngOnDestroy() {
    this.removeSeoSchemaScript();
  }

  private addImagePriority() {
    const href = '/assets/img/home-page-cvs.webp';

    const existingLink = this.document.head.querySelector(`link[rel="preload"][as="image"][href="${href}"]`);

    if (!existingLink) {
      const link = this.renderer2.createElement('link');

      this.renderer2.setAttribute(link, 'rel', 'preload');
      this.renderer2.setAttribute(link, 'as', 'image');
      this.renderer2.setAttribute(link, 'href', href);
      this.renderer2.setAttribute(link, 'fetchpriority', 'high');

      this.renderer2.appendChild(this.document.head, link);
    }
  }

  private addSeoSchemaScript() {
    this.removeSeoSchemaScript();

    let url = route_paths[this.translateService.currentLang as LngKeys].home;

    if (url === '/') {
      url = '';
    }

    const faq: TextContent_faq = {
      "@type": "FAQPage",
      "@id": `https://selfcv.com${url}/#faq`,
      "url": `https://selfcv.com${url}`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": this.translateService.instant("seo_schema.home_page.faq.q1_name"),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": this.translateService.instant("seo_schema.home_page.faq.q1_text"),
          }
        },
        {
          "@type": "Question",
          "name": this.translateService.instant("seo_schema.home_page.faq.q2_name"),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": this.translateService.instant("seo_schema.home_page.faq.q2_text"),
          }
        },
        {
          "@type": "Question",
          "name": this.translateService.instant("seo_schema.home_page.faq.q3_name"),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": this.translateService.instant("seo_schema.home_page.faq.q3_text"),
          }
        },
        {
          "@type": "Question",
          "name": this.translateService.instant("seo_schema.home_page.faq.q4_name"),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": this.translateService.instant("seo_schema.home_page.faq.q4_text"),
          }
        },
        {
          "@type": "Question",
          "name": this.translateService.instant("seo_schema.home_page.faq.q5_name"),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": this.translateService.instant("seo_schema.home_page.faq.q5_text"),
          }
        }
      ]
    };

    const textContent: TextContent = {
      "@context": "https://schema.org",
      "@graph": [
        structuredClone(seo_shema_org),
        structuredClone(
          get_seo_schema_website(
            this.translateService.currentLang as LngKeys,
            this.translateService.instant("seo_schema.home_page.website.desc")
          )
        ),
        structuredClone(get_seo_schema_app(this.translateService, 'seo_schema.home_page.app.desc')),
        structuredClone(faq)
      ]
    };

    addSeoSchemaScript(this.document, this.renderer2, textContent, this.seoSchemaName);
  }

  private removeSeoSchemaScript() {
    removeSeoSchemaScript(this.document, this.renderer2, this.seoSchemaName);
  }

}
